import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { AppContextProvider as DataProvider } from "@/shared/hooks/useAppContext";
import { IntercomProvider } from "./IntercomProvider";
import { DeceasedUser } from "@/shared/types/estate";

// Placeholder types for legacy context (may be removed in future refactor)
interface CareManager {
  id: number;
  name?: string;
}

interface Organization {
  id: number;
  name?: string;
}

export interface ISidebarContext {
  isCollapsed: boolean;
  setIsCollapsed: Dispatch<SetStateAction<boolean>>;
}

interface Plan {
  deceasedUser: DeceasedUser | null;
  careManager: CareManager | null;
  isCareManager: boolean;
}

export interface IPlan {
  plan: Plan;
  setPlan: React.Dispatch<SetStateAction<Plan>>;
}

interface IOrganization {
  organization: Organization | null;
  setOrganization: React.Dispatch<SetStateAction<Organization | null>>;
}

export interface IAppContext extends ISidebarContext, IPlan, IOrganization {}

export const AppContext = createContext<IAppContext | null>(null);

type AppProviderProps = PropsWithChildren<object>;

// TODO: refactor to use Zustand/Recoil/MobX
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [snackbarData, setSnackbarData] = React.useState<
    | {
        message: string;
        key: string;
      }
    | undefined
  >();

  const planFromLocalStorage = window.localStorage.getItem("plan");
  const initialPlan: Plan = planFromLocalStorage
    ? (JSON.parse(planFromLocalStorage) as Plan)
    : { deceasedUser: null, careManager: null, isCareManager: false };
  const [plan, setPlan] = useState<Plan>(initialPlan);

  React.useEffect(() => {
    window.localStorage.setItem("plan", JSON.stringify(plan));
  }, [plan]);

  const [organization, setOrganization] = useState<Organization | null>(null);

  const value: IAppContext = useMemo(
    () => ({
      isCollapsed,
      setIsCollapsed,
      snackbarData,
      setSnackbarData,
      closeSnackbar: () => setSnackbarData(undefined),
      plan,
      setPlan,
      organization,
      setOrganization,
    }),
    [
      isCollapsed,
      setIsCollapsed,
      snackbarData,
      setSnackbarData,
      plan,
      setPlan,
      organization,
      setOrganization,
    ],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppContext.Provider value={value}>
      <DataProvider>
        <IntercomProvider>{children}</IntercomProvider>
      </DataProvider>
    </AppContext.Provider>
  );
};

export function useAppContext(): IAppContext {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within a AppProvider");
  }
  return context;
}
